import type { LicenseTier } from '@/mocks/saLicensing';
import { licenseTypeIcons, licenseTypeBadge, formatStorage, formatBandwidth } from '@/mocks/saLicensing';

interface LicenseCardProps {
  license: LicenseTier;
  onEdit: (license: LicenseTier) => void;
  onDuplicate: (license: LicenseTier) => void;
  onToggleActive: (license: LicenseTier) => void;
  onToggleVisible: (license: LicenseTier) => void;
  onPreview: (license: LicenseTier) => void;
}

function priceLabel(lic: LicenseTier): string {
  if (lic.priceMonthly > 0) return `$${lic.priceMonthly}/mes`;
  if (lic.priceAnnual > 0) return `$${lic.priceAnnual}/año`;
  if (lic.priceLifetime > 0) return `$${lic.priceLifetime} único`;
  return 'Gratis';
}

function featureBadge(label: string, active: boolean) {
  return (
    <span
      className={`px-1.5 py-0.5 rounded text-[9px] font-medium whitespace-nowrap ${
        active ? 'bg-emerald-50 text-emerald-600 border border-emerald-200' : 'bg-background-100 text-foreground-300 border border-background-200'
      }`}
    >
      {label}
    </span>
  );
}

export default function LicenseCard({
  license,
  onEdit,
  onDuplicate,
  onToggleActive,
  onToggleVisible,
  onPreview,
}: LicenseCardProps) {
  const typeIcon = licenseTypeIcons[license.type];
  const typeBadge = licenseTypeBadge[license.type];

  const featureFlagsCount = license.featureFlags.length;
  const activeFeatures = [
    license.marketplace,
    license.tv,
    license.apps,
    license.ai,
    license.advertising,
    license.analytics,
    license.streaming,
    license.api,
  ].filter(Boolean).length;

  return (
    <div
      className={`relative bg-background-50 border-2 rounded-2xl p-5 flex flex-col transition-all hover:border-background-300 ${
        license.active ? 'border-background-200' : 'border-background-200 opacity-70'
      }`}
    >
      {/* Status badges */}
      <div className="absolute top-3 right-3 flex items-center gap-1.5">
        {!license.active && (
          <span className="px-2 py-0.5 rounded-full bg-red-50 text-red-600 text-[9px] font-semibold flex items-center gap-1">
            <i className="ri-pause-circle-line text-[9px]"></i>
            Inactivo
          </span>
        )}
        {!license.visible && (
          <span className="px-2 py-0.5 rounded-full bg-amber-50 text-amber-600 text-[9px] font-semibold flex items-center gap-1">
            <i className="ri-eye-off-line text-[9px]"></i>
            Oculto
          </span>
        )}
      </div>

      {/* Header */}
      <div className="text-center mb-4">
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-semibold ${typeBadge}`}>
          <i className={`${typeIcon} text-[10px]`}></i>
          {license.name}
        </span>
        <div className="text-foreground-950 font-bold text-xl mt-2">{priceLabel(license)}</div>
        {license.trialDays > 0 && (
          <div className="text-emerald-600 text-[10px] font-medium mt-0.5 flex items-center justify-center gap-1">
            <i className="ri-timer-line text-[9px]"></i>
            {license.trialDays} días de prueba
          </div>
        )}
        <p className="text-foreground-400 text-[10px] leading-relaxed mt-1.5 line-clamp-2">{license.description}</p>
      </div>

      {/* Key metrics */}
      <div className="mb-3 p-3 rounded-xl bg-background-100 grid grid-cols-2 gap-x-2 gap-y-2">
        <div className="flex items-center gap-1.5">
          <i className="ri-radio-line text-foreground-400 text-[10px]"></i>
          <span className="text-foreground-600 text-[10px]">
            {license.maxRadios === null ? '∞' : license.maxRadios} radios
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <i className="ri-user-line text-foreground-400 text-[10px]"></i>
          <span className="text-foreground-600 text-[10px]">
            {license.maxUsers === null ? '∞' : license.maxUsers} usuarios
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <i className="ri-hard-drive-2-line text-foreground-400 text-[10px]"></i>
          <span className="text-foreground-600 text-[10px]">{formatStorage(license.storageGB)}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <i className="ri-speed-up-line text-foreground-400 text-[10px]"></i>
          <span className="text-foreground-600 text-[10px]">{formatBandwidth(license.bandwidthGB)}</span>
        </div>
      </div>

      {/* Feature chips */}
      <div className="mb-3 flex flex-wrap gap-1">
        {featureBadge('Marketplace', license.marketplace)}
        {featureBadge('TV', license.tv)}
        {featureBadge('Apps', license.apps)}
        {featureBadge('IA', license.ai)}
        {featureBadge('Ads', license.advertising)}
        {featureBadge('Stats', license.analytics)}
        {featureBadge('API', license.api)}
      </div>

      {/* Feature flags count */}
      <div className="flex items-center gap-2 text-foreground-400 text-[10px] mb-4">
        <div className="flex items-center gap-1">
          <i className="ri-toggle-line text-[9px]"></i>
          <span><strong className="text-foreground-600">{featureFlagsCount}</strong> feature flags</span>
        </div>
        <div className="flex items-center gap-1">
          <i className="ri-check-double-line text-[9px]"></i>
          <span><strong className="text-foreground-600">{activeFeatures}/8</strong> features</span>
        </div>
      </div>

      {/* Metadata */}
      <div className="text-foreground-300 text-[9px] mb-4 space-y-0.5">
        <p>Creado: {license.createdAt}</p>
        <p>Actualizado: {license.updatedAt}</p>
      </div>

      {/* Actions */}
      <div className="mt-auto border-t border-background-200 pt-4 space-y-2">
        <div className="grid grid-cols-3 gap-2">
          <button
            onClick={() => onEdit(license)}
            className="flex items-center justify-center gap-1 px-2 py-2 rounded-lg bg-background-100 text-foreground-700 text-[10px] font-semibold hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-edit-line text-[10px]"></i>
            Editar
          </button>
          <button
            onClick={() => onDuplicate(license)}
            className="flex items-center justify-center gap-1 px-2 py-2 rounded-lg bg-background-100 text-foreground-700 text-[10px] font-semibold hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-file-copy-line text-[10px]"></i>
            Duplicar
          </button>
          <button
            onClick={() => onPreview(license)}
            className="flex items-center justify-center gap-1 px-2 py-2 rounded-lg bg-accent-50 text-accent-700 text-[10px] font-semibold hover:bg-accent-100 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-eye-line text-[10px]"></i>
            Preview
          </button>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={() => onToggleActive(license)}
            className={`flex items-center justify-center gap-1 px-3 py-2 rounded-lg text-[10px] font-semibold transition-colors cursor-pointer whitespace-nowrap ${
              license.active
                ? 'bg-amber-50 text-amber-700 hover:bg-amber-100'
                : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
            }`}
          >
            <i className={`${license.active ? 'ri-pause-circle-line' : 'ri-play-circle-line'} text-[10px]`}></i>
            {license.active ? 'Desactivar' : 'Activar'}
          </button>
          <button
            onClick={() => onToggleVisible(license)}
            className={`flex items-center justify-center gap-1 px-3 py-2 rounded-lg text-[10px] font-semibold transition-colors cursor-pointer whitespace-nowrap ${
              license.visible
                ? 'bg-amber-50 text-amber-700 hover:bg-amber-100'
                : 'bg-sky-50 text-sky-700 hover:bg-sky-100'
            }`}
          >
            <i className={`${license.visible ? 'ri-eye-off-line' : 'ri-eye-line'} text-[10px]`}></i>
            {license.visible ? 'Ocultar' : 'Publicar'}
          </button>
        </div>
        <button
          onClick={() => onDuplicate({ ...license, name: `${license.name} (copia)` })}
          className="w-full flex items-center justify-center gap-1 px-3 py-2 rounded-lg bg-violet-50 text-violet-600 text-[10px] font-semibold hover:bg-violet-100 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-git-branch-line text-[10px]"></i>
          Clonar configuración
        </button>
      </div>
    </div>
  );
}